import { InputCheckbox } from '@creditornot/cb-checkbox';
import { Typography, cssThemedValue } from '@creditornot/cb-ingredients';
import { useQuery } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { searchUsers } from 'apis/restaurant-api/merchant-admin/user/api';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import validateYupSchema from 'common/utils/validateYupSchema';
import { CampaignsQueryKeys } from 'modules/campaigns/campaignsQueryKeys';
import { TERMS_AND_CONDITIONS_LINK } from 'modules/campaigns/constants';
import {
  CampaignStatus,
  AdCampaignFormValues,
  AdCampaignBudgetFormValues,
  VenueAdCampaignConfigFormValues,
} from 'modules/campaigns/types';
import { shakingAnimationMixin } from 'modules/campaigns/utils/shakingCssAnimation';
import { adCampaignFormValidationSchema } from 'modules/campaigns/utils/validationSchemas/adCampaignFormValidationSchema';

import { TestIds } from '../../../utils/testUtils';
import CampaignFormLayout from '../../CampaignFormLayout';
import CopyCampaignIdButton from '../../CopyCampaignIdButton';
import { AdCampaignActionsFooter } from '../../ad-campaign/AdCampaignActionsFooter';
import { AdCampaignBudgetFormItem } from '../../ad-campaign/budget-section/AdCampaignBudgetFormItem';
import { AgreementsDetails } from '../../ad-campaign/budget-section/agreements-section/AgreementsDetails';
import { AdCampaignPreview } from '../../ad-campaign/preview-section/AdCampaignPreview';
import { AdCampaignScheduleSectionItem } from '../../ad-campaign/schedule-section/AdCampaignScheduleSectionItem';
import { CampaignFormUnsavedChangesModal } from '../../modals/CampaignFormUnsavedChangesModal/CampaignFormUnsavedChangesModal';

export type AdCampaignFormProps = {
  campaignAuthor?: string;
  venueAdsConfig: VenueAdCampaignConfigFormValues;
  campaignId?: string;
  campaignStatus?: CampaignStatus;
  initialValues: AdCampaignFormValues;
  isLoading: boolean;
  venueDetails?: Pick<Venue, 'name' | 'short_description' | 'listimage'>;
  onSubmit?: (values: AdCampaignFormValues) => void;
  onUpdate?: (values: Partial<AdCampaignFormValues>) => void;
  onCancelEdit?: () => void;
};

const StyledRoot = styled.div`
  max-width: 1120px;
  width: 100%;
  margin: 0 auto;
`;

const StyledCheckboxLabel = styled.label<{ isShaking?: boolean }>`
  display: flex;
  gap: 8px;
  color: ${cssThemedValue('textPrimary')};
  ${({ isShaking }) => (isShaking ? shakingAnimationMixin : 'animation: none')}
`;

const StyledAgreementsWrapper = styled.div`
  display: flex;
  border-radius: 8px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 16px 0;
`;

export const AdCampaignForm = ({
  campaignId,
  campaignAuthor,
  campaignStatus,
  venueAdsConfig,
  initialValues,
  isLoading,
  venueDetails,
  onSubmit,
  onUpdate,
  onCancelEdit,
}: AdCampaignFormProps) => {
  const { formatMessage } = useIntl();
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState<boolean>(false);

  const [shouldShake, setShouldShake] = useState<boolean>(false);

  const { data: author, isFetching: isLoadingAuthor } = useQuery(
    CampaignsQueryKeys.GET_AUTHOR(campaignAuthor!),
    async () => {
      const { users } = await searchUsers(campaignAuthor!);
      return users.find(user => user.id === campaignAuthor);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!campaignId,
    },
  );

  const handleSubmitCampaign = (values: AdCampaignFormValues) => {
    if (campaignId && onUpdate) {
      // only keep updated values
      const editedValues = Object.fromEntries(
        Object.entries(values).filter(([key, value]) => value !== initialValues[key as keyof AdCampaignFormValues]),
      );

      return onUpdate(editedValues);
    }

    if (values.agreementTermsAndConditions && onSubmit) {
      return onSubmit({
        ...values,
        campaignId,
      });
    }

    setShouldShake(false);
    // Timeout to prevent react from batching state setters
    setTimeout(() => setShouldShake(true), 0);
  };

  const hasCampaignStarted = () => {
    if (!campaignId || campaignStatus === CampaignStatus.SCHEDULED) {
      return false;
    }

    const isStartDateInFuture = initialValues.startDate > DateTime.local().toISO();
    if (campaignStatus === CampaignStatus.STOPPED && isStartDateInFuture) {
      return false;
    }

    return true;
  };

  const {
    budgetOptions,
    currency,
    maximumCustomBidAmount,
    minimumBidAmount,
    minimumBudgetAmount,
    maximumBudgetAmount,
  } = venueAdsConfig!;

  return (
    <StyledRoot data-testid="ads-form">
      <Form<AdCampaignFormValues>
        onSubmit={handleSubmitCampaign}
        initialValues={initialValues}
        validate={() => validateYupSchema(adCampaignFormValidationSchema)}
      >
        {({ handleSubmit, form, values }) => {
          // using 'dirty' prop does not change for some reason when making changes to schedule so instead use 'isEqual' to compare the values
          const isDirty = !isEqual(values, initialValues);
          return (
            <CampaignFormLayout
              preview={<AdCampaignPreview venueDetails={venueDetails} />}
              onFormSubmit={handleSubmit}
              tooltip="campaigns.sidebar-preview.ads.tooltip"
              isFloatingBannerEnabled
              infoBannerContent={{
                title: formatMessage({ id: 'campaigns.ads.info-banner.title' }),
                text: formatMessage({ id: 'campaigns.ads.info-banner.text' }),
                campaignId: campaignId ?? 'NewCampaign',
              }}
            >
              <CampaignFormUnsavedChangesModal
                isOpen={isUnsavedChangesModalOpen}
                setIsOpen={setIsUnsavedChangesModalOpen}
              />
              <AdCampaignBudgetFormItem
                initialValues={{
                  budgetAmount: values.budgetAmount,
                  budgetType: values.budgetType,
                  bidStrategy: values.bidStrategy,
                  customBidAmount: values.customBidAmount,
                }}
                budgetOptions={[...budgetOptions, { type: 'custom', value: 0 }]}
                campaignId={campaignId}
                isDisabled={campaignStatus === CampaignStatus.COMPLETED}
                hasCampaignStarted={hasCampaignStarted()}
                onUpdate={(formValues: AdCampaignBudgetFormValues) => {
                  form.batch(() => {
                    form.change('budgetAmount', formValues.budgetAmount);
                    form.change('bidStrategy', formValues.bidStrategy);
                    form.change('customBidAmount', formValues.customBidAmount);
                    form.change('budgetType', formValues.budgetType);
                  });
                }}
                maxBidAmount={convertMonetaryIntToDecimal(maximumCustomBidAmount, currency)}
                minBidAmount={convertMonetaryIntToDecimal(minimumBidAmount, currency)}
                minWeeklyBudget={convertMonetaryIntToDecimal(minimumBudgetAmount, currency)}
                maxWeeklyBudget={convertMonetaryIntToDecimal(maximumBudgetAmount, currency)}
              />
              <AdCampaignScheduleSectionItem
                campaignId={campaignId}
                initialValues={{
                  startDate: values.startDate,
                  endDate: values.endDate,
                  scheduleStrategy: values.scheduleStrategy,
                }}
                campaignStatus={campaignStatus}
                onUpdate={values => {
                  form.batch(() => {
                    form.change('startDate', values.startDate);
                    form.change('endDate', values.endDate);
                    form.change('scheduleStrategy', values.scheduleStrategy);
                  });
                }}
                hasCampaignStarted={hasCampaignStarted()}
              />
              {!!campaignId ? (
                <AgreementsDetails
                  agreements={[
                    <FormattedMessage
                      id="campaigns.agreements.terms-and-conditions-statement"
                      values={{
                        link: (
                          <a href={TERMS_AND_CONDITIONS_LINK} target="_blank" rel="noreferrer">
                            <FormattedMessage id="campaigns.agreements.terms-and-conditions-link" />
                          </a>
                        ),
                      }}
                    />,
                  ]}
                  isLoading={isLoadingAuthor}
                  author={author}
                  isCreatedByWolt={author?.email.endsWith('@wolt.com')} // TODO: replade with campaign.created_by_wolt when it will be available
                />
              ) : (
                <StyledAgreementsWrapper data-testid={TestIds.FORM.AGREEMENTS.WRAPPER}>
                  <Field<AdCampaignFormValues['agreementTermsAndConditions']>
                    name="agreementTermsAndConditions"
                    type="checkbox"
                  >
                    {({ input }) => (
                      <StyledCheckboxLabel isShaking={shouldShake && !input.checked}>
                        <InputCheckbox
                          name={input.name}
                          checked={input.checked}
                          onChange={input.onChange}
                          disabled={!!campaignId}
                        />
                        <Typography.Body3>
                          <FormattedMessage
                            id="campaigns.agreements.terms-and-conditions-statement"
                            values={{
                              link: (
                                <a href={TERMS_AND_CONDITIONS_LINK} target="_blank" rel="noreferrer">
                                  <FormattedMessage id="campaigns.agreements.terms-and-conditions-link" />
                                </a>
                              ),
                            }}
                          />
                        </Typography.Body3>
                      </StyledCheckboxLabel>
                    )}
                  </Field>
                </StyledAgreementsWrapper>
              )}
              <AdCampaignActionsFooter
                campaignId={campaignId}
                isDirty={isDirty}
                isLoading={isLoading}
                onSubmit={handleSubmit}
                onCancel={onCancelEdit}
              />
            </CampaignFormLayout>
          );
        }}
      </Form>

      {campaignId && <CopyCampaignIdButton dataTestId={TestIds.FORM.ID.WRAPPER} campaignId={campaignId} />}
    </StyledRoot>
  );
};
